/**
 * 首页 - UI/UX Pro Max 优化版
 *
 * 设计系统：
 * - Hero + Features + CTA 模式
 * - Soft UI Evolution 风格
 * - 清晰的视觉层次
 *
 * 优化点：
 * 1. 更强大的 Hero 区域
 * 2. 双入口 CTA（对标爆款 / 上传拆书）
 * 3. 功能卡片交互优化
 * 4. 社交证明展示
 */

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Target,
  ChevronRight,
  Star,
  BookOpen,
  Wand2,
} from 'lucide-react';

// 核心功能特性 - V3 相关功能展示
const features = [
  {
    icon: Sparkles,
    title: '智能拆书 V3',
    description: '上传完整小说，AI 提取写作规则，生成个性化大纲',
    color: 'from-emerald-500 to-emerald-600',
  },
  {
    icon: Target,
    title: '爆款结构分析',
    description: '三幕式、黄金三章、爽点分布，精准拆解爆款套路',
    color: 'from-blue-500 to-blue-600',
  },
  {
    icon: Wand2,
    title: '一键生成',
    description: '30秒生成完整大纲，从书名到黄金三章',
    color: 'from-orange-500 to-orange-600',
  },
  {
    icon: BookOpen,
    title: '对标爆款',
    description: '选择一本爆款作为参考，AI 分析其结构和爽点设计',
    color: 'from-purple-500 to-purple-600',
  },
];

// 入口卡片 - 简化为 2 个核心入口
const entryPoints = [
  {
    title: '智能拆书 V3',
    description: '上传完整小说，AI 提取写作规则，基于规则生成大纲和黄金三章',
    icon: Sparkles,
    path: '/v3',
    color: 'from-emerald-500 to-emerald-600',
    featured: true,
  },
  {
    title: '对标爆款',
    description: '选择一本爆款作为参考，AI 分析其结构和爽点设计',
    icon: BookOpen,
    path: '/reference',
    color: 'from-primary-500 to-primary-600',
    featured: false,
  },
];

// 案例展示
const examples = [
  {
    title: '都市重生之商界大佬',
    genre: '都市',
    rating: 8.5,
    logline: '重生回到2010年，用前世记忆打造商业帝国',
  },
  {
    title: '修仙从炼丹开始',
    genre: '玄幻',
    rating: 7.8,
    logline: '废柴少年觉醒丹道天赋，逆天改命',
  },
  {
    title: '星际指挥官',
    genre: '科幻',
    rating: 8.2,
    logline: '穿越星际成为舰队指挥官，征服银河',
  },
];

// 用户评价
const testimonials = [
  {
    name: '网文作者小王',
    content: '以前写大纲要憋好几天，现在30秒就有了框架',
    rating: 5,
  },
  {
    name: '新手作家阿林',
    content: '生成的爽点分布很合理，节奏感强',
    rating: 5,
  },
];

export default function Home() {
  return (
    <div className="min-h-[calc(100vh-200px)]">
      {/* Hero Section */}
      <section className="py-16 px-4 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* 标签 */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-purple-50 text-primary-700 px-4 py-2 rounded-full mb-6 border border-primary-100">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">AI 驱动的网文大纲生成</span>
            </div>

            {/* 标题 */}
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-slate-900">
              一键生成爆款大纲
            </h1>

            {/* 副标题 */}
            <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              选择爆款参考或上传文本，30秒获得完整的网文大纲。
              <br className="hidden md:block" />
              包含书名、人物设定、三幕结构、爽点分布。
            </p>

            {/* 双入口 CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/v3"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white text-lg font-semibold px-8 py-4 rounded-xl shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-200 cursor-pointer"
              >
                <Sparkles className="w-5 h-5" />
                智能拆书 V3
                <ChevronRight className="w-5 h-5" />
              </Link>
              <Link
                to="/reference"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-700 text-lg font-semibold px-8 py-4 rounded-xl shadow-lg border border-slate-200 hover:border-slate-300 transition-all duration-200 cursor-pointer"
              >
                <BookOpen className="w-5 h-5" />
                对标爆款
              </Link>
            </div>

            {/* 信任提示 */}
            <p className="mt-4 text-sm text-slate-400">
              无需注册 · 免费试用 · 30秒生成
            </p>
          </motion.div>
        </div>
      </section>

      {/* 入口卡片区域 */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {entryPoints.map((entry, index) => (
              <motion.div
                key={entry.path}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={entry.path}
                  className={`block bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg border-2 transition-all duration-200 cursor-pointer ${
                    entry.featured
                      ? 'border-primary-200 hover:border-primary-400'
                      : 'border-slate-200 hover:border-purple-400'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${entry.color} flex items-center justify-center shrink-0`}>
                      <entry.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
                        {entry.title}
                        {entry.featured && (
                          <span className="text-xs bg-primary-100 text-primary-600 px-2 py-0.5 rounded-full">
                            推荐
                          </span>
                        )}
                      </h3>
                      <p className="text-slate-500 text-sm">{entry.description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 核心特性 */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-slate-900 mb-2">核心功能</h2>
          <p className="text-slate-500 text-center mb-8">AI 驱动的专业网文创作工具</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-5 border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200 cursor-pointer"
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center mb-3`}>
                  <feature.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                <p className="text-slate-500 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 案例展示 */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-slate-900 mb-2">生成效果预览</h2>
          <p className="text-slate-500 text-center mb-8">看看 AI 生成的大纲长什么样</p>

          <div className="grid md:grid-cols-3 gap-4">
            {examples.map((example, index) => (
              <motion.div
                key={example.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:border-primary-300 hover:shadow-md transition-all duration-200 cursor-pointer"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-full">
                    {example.genre}
                  </span>
                  <div className="flex items-center gap-1 text-amber-500">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="text-sm font-medium text-slate-700">{example.rating}</span>
                  </div>
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{example.title}</h3>
                <p className="text-sm text-slate-500 line-clamp-2">{example.logline}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 用户评价 */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-slate-900 mb-8">用户怎么说</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {testimonials.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-5 border border-slate-200"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(item.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-current" />
                  ))}
                </div>
                <p className="text-slate-600 text-sm mb-3">"{item.content}"</p>
                <p className="text-xs text-slate-400">— {item.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 底部 CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-emerald-500 to-emerald-600">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">准备好开始了吗？</h2>
          <p className="text-emerald-100 mb-6">
            上传你的小说，AI 提取写作规则，生成个性化大纲
          </p>
          <Link
            to="/v3"
            className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-emerald-600 font-semibold px-8 py-4 rounded-xl shadow-lg transition-all duration-200 cursor-pointer"
          >
            <Sparkles className="w-5 h-5" />
            开始智能拆书
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
